import { parsePlainDecimal } from './parseBidAmount'
import type { ParsedBidSheetRow } from './types'

/**
 * A manifest-level sheet's lot row and its per-unit manifest lines all carry
 * the same Listing ID (see `buildDownloadableBidSheetCsv`) — grouping by that
 * shared key is what lets a filled-in per-unit line roll up into its lot's
 * total instead of being read as a stray row with no lot-level bid.
 *
 * Blank Listing IDs are never grouped together — each one stays its own
 * group of one row, so `validateBidSheetRows` can still report
 * 'missingListingId' per row instead of merging unrelated malformed rows
 * into a single blended result.
 */
export type LotBidGroup = {
  /**
   * Every row that shares this group's Listing ID, in file order.
   */
  rows: ParsedBidSheetRow[]
  /**
   * The rows (if any) competing to be this lot's bid — a subset of `rows`,
   * always excluding any per-unit-only manifest line. A row qualifies by
   * either holding a genuine lot-level bid or having been directly edited
   * on the review screen (even down to blank — an edit is a deliberate bid
   * attempt in progress, not "no bid"). When `isDuplicateLotBid` or
   * `conflictResolved` is true, `validateBidSheetRows` reports each of
   * *these* individually (not all of `rows`) instead of folding to one
   * representative — a per-unit-only sibling line has no bid of its own to
   * conflict over, so it stays folded away even when its lot's real bids
   * are in conflict.
   */
  conflictingRows: ParsedBidSheetRow[]
  /**
   * The row whose identity (`rowNumber`, `rawRow`) represents this lot in
   * the review table and the After-Upload Report: the one active, non-blank
   * lot-level bid if there's exactly one, otherwise the first row in file
   * order among whichever set applies (`conflictingRows`, or `rows` when
   * there are no candidates at all). Every other row in the group is folded
   * into this one, except when `isDuplicateLotBid` or `conflictResolved`
   * is true.
   */
  representative: ParsedBidSheetRow
  /**
   * Effective Bid Submission for this lot: the representative row's own raw
   * text when it holds a genuine lot-level bid, otherwise the summed
   * per-unit total (stringified) when one exists, otherwise `''`. This is
   * what the review screen displays and edits — typing over a per-unit
   * total in that cell sets a lot-level bid for this lot, same as typing
   * into any other row.
   */
  bidSubmissionRaw: string
  /**
   * Sum of every finite, positive per-unit bid in the group. `null` when
   * the group has no per-unit bid entries at all (an ordinary, non-manifest
   * row, or a manifest lot whose lines were all left blank) — distinct from
   * `0`, which would mean every entry parsed but summed to nothing.
   * Non-numeric per-unit entries are excluded from this sum, but they flag
   * the lot (see `PerUnitSum.hadInvalidEntry`) so it reports invalidAmount
   * instead of trusting a partial total.
   */
  perUnitTotal: number | null
  /**
   * True when this lot had both an explicit lot-level bid and a positive
   * per-unit total — the lot-level bid is what `bidSubmissionRaw` carries;
   * this only flags that a per-unit total was also present and overridden,
   * so the review screen can surface that precedence.
   */
  usedLotBidOverPerUnit: boolean
  /**
   * More than one row in the group currently holds its own non-blank
   * lot-level bid, with more than one still active (neither removed nor
   * cleared down to blank by an edit) — a genuine, unresolved conflict the
   * lot-bid-wins rule doesn't resolve (that rule only applies between a lot
   * bid and a per-unit total, not between two lot bids). Also true when
   * every such bid has been removed, leaving no winner to pick.
   */
  isDuplicateLotBid: boolean
  /**
   * The group once had more than one active, non-blank lot-level bid, but
   * removing one (or clearing it via an edit) brought it down to exactly
   * one survivor — `representative` is that row, which `validateBidSheetRows`
   * validates normally. Every other candidate is still emitted as its own
   * row rather than folded away (so a removed row keeps its place in the
   * table), but only one still holding a competing non-blank bid, or itself
   * removed, reports 'duplicate' — a merely-cleared sibling just validates
   * on its own (typically invalidAmount), since it's no longer competing
   * for anything.
   */
  conflictResolved: boolean
}

// Rounds to the nearest cent after each addition — summing plain JS floats
// (e.g. 0.1 + 0.2) drifts into values like 0.30000000000000004, which would
// otherwise flow straight into the displayed total, the belowMinimum
// comparison, and the amount actually bid.
const roundToCents = (amount: number): number => Math.round(amount * 100) / 100

type PerUnitSum = {
  total: number | null
  /**
   * True when a per-unit entry was non-blank but still not a usable
   * number (e.g. "1,000" or "$10" — Excel-mangled or currency-formatted
   * text, same bug class `validateBidSheetRows` already guards against for
   * a lot-level bid). Unlike a blank entry, this isn't "untouched" — it's
   * data the buyer did fill in that couldn't be read, so silently treating
   * it as zero would quietly under-bid the lot. The caller surfaces this as
   * an error instead of trusting `total`.
   */
  hadInvalidEntry: boolean
}

// perUnitBidRaw is a single-unit price, not the line's full contribution —
// matches this domain's existing Unit Price -> Ext. Price convention
// elsewhere (a line total is always unit price times quantity). A blank or
// zero/negative Units value defaults to a quantity of 1 rather than
// discarding the line, so a buyer who left Units blank on a single-unit line
// still gets a usable rollup instead of a silently dropped bid — but a
// non-blank Units value that isn't a number at all flags the lot the same
// way an unreadable Per-Unit Bid does, rather than silently defaulting.
const sumPerUnitBids = (rows: ParsedBidSheetRow[]): PerUnitSum => {
  let total: number | null = null
  let hadInvalidEntry = false
  for (const row of rows) {
    if (!row.perUnitBidRaw) continue
    // parsePlainDecimal (not Number()) so a hex ('0x10') or exponential
    // ('1e2') Per-Unit Bid cell is flagged instead of silently read as a
    // decimal — the same malformed-input class validateGroupRow guards
    // against for the lot-level Bid Submission column.
    const unitPrice = parsePlainDecimal(row.perUnitBidRaw)
    if (unitPrice === null) {
      // Non-blank text that isn't a plain decimal at all (e.g. "1,000",
      // "$10", "0x10") — distinct from a valid zero/negative entry just
      // below, which is a deliberate "no bid on this line" and stays
      // silently skipped.
      hadInvalidEntry = true
      continue
    }
    if (unitPrice <= 0) continue
    const parsedQuantity = row.unitsRaw ? parsePlainDecimal(row.unitsRaw) : null
    if (row.unitsRaw && parsedQuantity === null) {
      // Same reasoning as an unreadable Per-Unit Bid above — a non-blank
      // Units cell that doesn't parse (e.g. "1,000", "0x5") would otherwise
      // silently fall back to a quantity of 1 and under-bid the line with
      // no indication why.
      hadInvalidEntry = true
      continue
    }
    const quantity =
      parsedQuantity !== null && parsedQuantity > 0 ? parsedQuantity : 1
    total = roundToCents((total ?? 0) + unitPrice * quantity)
  }
  return { total, hadInvalidEntry }
}

export const groupBidSheetRowsByLot = (
  rows: ParsedBidSheetRow[],
  // Row numbers the buyer has directly edited on the review screen (see
  // UploadBidSheetModal's editedBidSubmissions). Once a row's Bid
  // Submission has been explicitly edited, it's treated as a genuine
  // lot-level bid attempt even if the buyer clears it back to blank —
  // without this, a rolled-up lot's input snaps back to the per-unit total
  // the instant it's cleared, making it impossible to type a fresh amount.
  editedRowNumbers: ReadonlySet<number> = new Set(),
  // Row numbers the buyer has dismissed on the review screen. Excluded when
  // deciding whether two lot-level bids still conflict — removing one of a
  // conflicting pair should let the other resolve on its own, without also
  // requiring the buyer to edit it.
  removedRowNumbers: ReadonlySet<number> = new Set()
): LotBidGroup[] => {
  const groupsByListingId = new Map<string, ParsedBidSheetRow[]>()
  // One entry per eventual group, in file order — a blank-Listing-ID row
  // (its own group of one) and a Listing ID's first-seen row both claim
  // their position here, so the final output stays in upload order instead
  // of every grouped lot sorting ahead of every blank-ID row.
  const groupRowLists: ParsedBidSheetRow[][] = []

  for (const row of rows) {
    if (!row.listingId) {
      groupRowLists.push([row])
      continue
    }
    const existing = groupsByListingId.get(row.listingId)
    if (existing) {
      existing.push(row)
    } else {
      const groupRows: ParsedBidSheetRow[] = [row]
      groupsByListingId.set(row.listingId, groupRows)
      groupRowLists.push(groupRows)
    }
  }

  const buildGroup = (groupRows: ParsedBidSheetRow[]): LotBidGroup => {
    // A row qualifies as competing for this lot's bid either by holding a
    // genuine lot-level bid, or by having been directly edited on the
    // review screen — even down to blank. Without the edit half of this,
    // a buyer who clears a row to type a fresh amount could lose that row
    // entirely the instant some *other* row in the group still has a real
    // bid (it would no longer look like a candidate at all, and silently
    // drop out of both the table and the group's own history).
    const lotBidCandidates = groupRows.filter(
      (row) =>
        row.bidSubmissionRaw !== '' || editedRowNumbers.has(row.rowNumber)
    )
    const { total: perUnitTotal, hadInvalidEntry } = sumPerUnitBids(groupRows)

    if (lotBidCandidates.length > 0) {
      // Excludes a dismissed row — removing one of a conflicting pair
      // should let the other resolve on its own. Further narrowed to rows
      // still holding a non-blank bid — a candidate the buyer cleared to
      // type a fresh amount isn't competing for anything anymore either,
      // so clearing one of two conflicting bids resolves the conflict just
      // like removing one does, without requiring both.
      const activeBidRows = lotBidCandidates.filter(
        (row) =>
          row.bidSubmissionRaw !== '' && !removedRowNumbers.has(row.rowNumber)
      )
      const representative =
        activeBidRows.length === 1
          ? (activeBidRows[0] as ParsedBidSheetRow)
          : (lotBidCandidates[0] as ParsedBidSheetRow)
      return {
        rows: groupRows,
        conflictingRows: lotBidCandidates,
        representative,
        bidSubmissionRaw: representative.bidSubmissionRaw,
        perUnitTotal,
        // Suppressed when an unparseable per-unit entry means perUnitTotal
        // is only a partial sum — the lot bid wins either way, but the note
        // shouldn't claim it overrode a total that silently left a line out.
        usedLotBidOverPerUnit: !hadInvalidEntry && (perUnitTotal ?? 0) > 0,
        // Unresolved whenever there was ever more than one candidate and
        // exactly one active, non-blank survivor hasn't emerged — covers
        // both "still 2+ active bids" and "every active bid got cleared or
        // removed, leaving none" (that second case must still emit every
        // row individually below, not fold to whichever one happens to be
        // first, or the others would silently vanish from the table).
        isDuplicateLotBid:
          lotBidCandidates.length > 1 && activeBidRows.length !== 1,
        conflictResolved:
          lotBidCandidates.length > 1 && activeBidRows.length === 1,
      }
    }

    // No row in this group competes for a lot-level bid at all. The first
    // row in file order represents the group — normally that's the lot row
    // itself, with nothing to show but the per-unit total.
    const representative = groupRows[0] as ParsedBidSheetRow
    return {
      rows: groupRows,
      conflictingRows: [],
      representative,
      // A per-unit entry that couldn't be parsed (not just left blank)
      // forces this blank rather than trusting a partial sum — a buyer who
      // typed "$10" or "1,000" on one line should see this lot as needing
      // attention, not get a quietly under-counted total.
      bidSubmissionRaw:
        !hadInvalidEntry && perUnitTotal !== null && perUnitTotal > 0
          ? String(perUnitTotal)
          : '',
      perUnitTotal,
      usedLotBidOverPerUnit: false,
      isDuplicateLotBid: false,
      conflictResolved: false,
    }
  }

  return groupRowLists.map(buildGroup)
}
